import { Component, ViewChild, ElementRef, EventEmitter, Output, Input, OnChanges, OnInit } from '@angular/core';
import * as _ from 'lodash';
import { CREDIT_APP_TOAST_MESSAGES, FILE_UPLOAD_TOAST_MESSAGES, FileUploadConfig } from '../../constants/common';
import { NUMBER } from 'src/app/feature/ar-portal/enum/ar-portal.enum';

@Component({
    selector: 'app-upload-file',
    templateUrl: './upload-file.component.html',
    styleUrls: ['./upload-file.component.scss'],
    standalone: false
})
export class UploadFileComponent implements  OnInit, OnChanges {

  @Input() allowOnlySingleFile: boolean = false;
  @Input() viewFileExternal: boolean = false;
  @Input() section: string;
  @Input() uploadedFiles: File[] = [];
  @Input() disabled: boolean = false;
  @Input() fileUploadSuccess: boolean = false;
  @Input() userUploadedFileName;
  @Output() filesChanged = new EventEmitter<File[]>();
  @Output() callbackMessage = new EventEmitter<any>();
  @Output() fileRemoved = new EventEmitter<File[]>();
  @ViewChild('fileInput') fileInput!: ElementRef;
  @Output() filesUpdated = new EventEmitter<File[]>();
  @Output() viewFileCallback = new EventEmitter<any>();
  @Output() fileExceedsLimit = new EventEmitter<any>();
  fileErrors: string[] = [];
  constants = FileUploadConfig;
  isFileOver = false;
  files: File[] = [];
  fileSizeErrorMessage: string;
  multipleFilesErrorMessage: string;
  pdfFileErrorMessage: string;
  fileNameErrorMessage: string | null = null;
  combinedSize: number = NUMBER.zero;

  get MAX_FILE_SIZE(): number {
    if (this.section === 'coversheet' || this.section === 'letterofguarantee' || this.section === 'certificateOfInsurance') {
      return 25 * 1024 * 1024;
    } else if (this.section === 'sendloa' || this.section === 'bank' || this.section === 'trade' || this.section === 'projectCoi') {
      return 20 * 1024 * 1024;
    } else {
      return 5 * 1024 * 1024;
    }
  }

  /**
   * ngOnInit() - Initializes the component, setting up the initial state and file upload success status.
   */
  ngOnInit() {
    this.files = this.uploadedFiles || [];
    this.fileUploadSuccess = this.files ? true : false;
  }

  /**
   * ngOnChanges() - Handles changes to the input properties, particularly the uploaded files.
   */
  ngOnChanges() {
    this.files = this.uploadedFiles || [];
    this.fileUploadSuccess = this.files ? true : false;
  }

  /**
   * onDragOver() - Handles the drag over event to indicate a file drag action.
   * @param event - The drag event.
   */
  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isFileOver = true;
  }

  /**
   * onDragLeave() - Handles the drag leave event to reset drag action indicator.
   * @param event - The drag event.
   */
  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isFileOver = false;
  }

  /**
   * onDrop() - Handles the drop event to add the dragged files.
   * @param event - The drop event.
   */
  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isFileOver = false;
    if (this.allowOnlySingleFile) {
      const files = event.dataTransfer?.files;
      this.fileSizeErrorMessage = null;
      this.multipleFilesErrorMessage = null;
      this.pdfFileErrorMessage = null;
      const selectedFile = files[0];
      if (files && files?.length > 1) {
        // If multiple files are dropped, show an error and prevent further action
        this.resetFileInput();
        this.multipleFilesErrorMessage = CREDIT_APP_TOAST_MESSAGES.dropOnlyOneFile;
        this.callbackMessage.emit({ message: FILE_UPLOAD_TOAST_MESSAGES.OnlyOneFileIsAllowed });
        this.fileSizeErrorMessage = null;
        this.pdfFileErrorMessage = null;
        this.files = null;
        return;
      }
      if (selectedFile?.size > this.MAX_FILE_SIZE) {
        this.resetFileInput();
        this.fileSizeErrorMessage = this.section === 'sendloa' || this.section === 'bank' || this.section === 'trade' || this.section === 'projectCoi' ? CREDIT_APP_TOAST_MESSAGES.fileMoreThan20MBErrMsg :
          this.section === 'coversheet' || this.section === 'letterofguarantee' || this.section === 'certificateOfInsurance'
            ? CREDIT_APP_TOAST_MESSAGES.fileMoreThan25MBErrMsg
            : CREDIT_APP_TOAST_MESSAGES.fileMoreThan5MBErrMsg;
        this.section === 'sendloa' || this.section === 'bank' || this.section === 'trade' || this.section === 'projectCoi' ? this.callbackMessage.emit({ message: FILE_UPLOAD_TOAST_MESSAGES.sizeLimit20MBErrMsg }) : this.callbackMessage.emit({ message: FILE_UPLOAD_TOAST_MESSAGES.sizeLimit25MBErrMsg });
        this.multipleFilesErrorMessage = null;
        this.pdfFileErrorMessage = null;
        this.files = this.files || [];
        return;
      }
      if (selectedFile?.type !== 'application/pdf') {
        // If other than pdf file is drop, show an error and prevent further action
        this.resetFileInput();
        this.pdfFileErrorMessage = CREDIT_APP_TOAST_MESSAGES.selectPdfFile;
        this.callbackMessage.emit({ message: FILE_UPLOAD_TOAST_MESSAGES.invalidFileType });
        this.fileSizeErrorMessage = null;
        this.multipleFilesErrorMessage = null;
        this.files = null;
        return;
      }
      const validFileNameRegex = /^[a-zA-Z0-9_\-\s]+\.pdf$/;
      if (!validFileNameRegex.test(selectedFile.name)) {
        this.resetFileInput();
        this.fileNameErrorMessage = CREDIT_APP_TOAST_MESSAGES.invalidFileName;
        return;
      }
      if (selectedFile) {
        this.fileSizeErrorMessage = null;
        this.multipleFilesErrorMessage = null;
        this.pdfFileErrorMessage = null;
        this.files = [selectedFile]; // Ensure only one file is stored
        this.emitFiles();
      }

    }
    else if (event.dataTransfer?.files) {
      this.fileSizeErrorMessage = null;
      this.multipleFilesErrorMessage = null;
      this.pdfFileErrorMessage = null;
      const selectedFiles = event.dataTransfer.files;
      const allAvailableFiles = selectedFiles?.length + this.files?.length;
      if (this.section === 'sendloa' && selectedFiles?.length > 5 || this.section === 'sendloa' && allAvailableFiles > 5) {
        this.resetFileInput();
        this.multipleFilesErrorMessage = CREDIT_APP_TOAST_MESSAGES.max5FilesLimit;
        this.files = null;
        return;
      } else if (this.section === 'coversheet' && selectedFiles?.length > 10 || this.section === 'coversheet' && allAvailableFiles > 10) {
        this.resetFileInput();
        this.multipleFilesErrorMessage = CREDIT_APP_TOAST_MESSAGES.max10FilesLimit;
        this.files = null;
        return;
      }
      const isValid = this.validateMulipleFiles(selectedFiles);
      if (isValid) {
        this.addFiles(selectedFiles);
      } else {
        this.resetFileInput();
      }
    }

  }

  /**
   * onFileSelect() - Handles the file input change event to add selected files.
   * @param event - The input event.
   */
  onFileSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    this.fileSizeErrorMessage = null;
    this.multipleFilesErrorMessage = null;
    this.pdfFileErrorMessage = null;
    this.fileNameErrorMessage = null;

    if (this.allowOnlySingleFile) {
      if (input?.files) {
        const selectedFile = input?.files[0];
        if (selectedFile?.size > this.MAX_FILE_SIZE) {
          this.resetFileInput();
          this.fileSizeErrorMessage = this.section === 'sendloa' || this.section === 'bank' || this.section === 'trade' || this.section === 'projectCoi' ? CREDIT_APP_TOAST_MESSAGES.fileMoreThan20MBErrMsg :
            this.section === 'coversheet' || this.section === 'letterofguarantee' || this.section === 'certificateOfInsurance'
              ? CREDIT_APP_TOAST_MESSAGES.fileMoreThan25MBErrMsg
              : CREDIT_APP_TOAST_MESSAGES.fileMoreThan5MBErrMsg;
          this.section === 'sendloa' || this.section === 'bank' || this.section === 'trade' || this.section === 'projectCoi' ? this.callbackMessage.emit({ message: FILE_UPLOAD_TOAST_MESSAGES.sizeLimit20MBErrMsg }) :
            this.callbackMessage.emit({ message: FILE_UPLOAD_TOAST_MESSAGES.sizeLimit25MBErrMsg });
          this.multipleFilesErrorMessage = null;
          this.pdfFileErrorMessage = null;
          this.files = this.files || [];
          return;
        }
        if (selectedFile && selectedFile.type !== 'application/pdf') {
          this.resetFileInput();
          this.pdfFileErrorMessage = CREDIT_APP_TOAST_MESSAGES.selectPdfFile;
          this.callbackMessage.emit({ message: FILE_UPLOAD_TOAST_MESSAGES.invalidFileType });
          this.fileSizeErrorMessage = null;
          this.multipleFilesErrorMessage = null;
          this.files = null;
          return;
        }

        const validFileNameRegex = /^[a-zA-Z0-9_\-\s]+\.pdf$/;
        if (!validFileNameRegex.test(selectedFile.name)) {
          this.resetFileInput();
          this.fileNameErrorMessage = CREDIT_APP_TOAST_MESSAGES.invalidFileName;
          return;
        }

        if (selectedFile) {
          this.fileSizeErrorMessage = null;
          this.multipleFilesErrorMessage = null;
          this.pdfFileErrorMessage = null;
          this.files = [selectedFile];
          this.emitFiles();
        }
      }
    }
    else if (input?.files) {
      this.fileSizeErrorMessage = null;
      this.multipleFilesErrorMessage = null;
      this.pdfFileErrorMessage = null;
      const selectedFiles = input?.files;
      const allAvailableFiles = selectedFiles?.length + this.files?.length;
      if (this.section === 'sendloa' && selectedFiles?.length > 5 || this.section === 'sendloa' && allAvailableFiles > 5) {
        this.resetFileInput();
        this.multipleFilesErrorMessage = CREDIT_APP_TOAST_MESSAGES.max5FilesLimit;
        this.files = null;
        return;
      } else if (this.section === 'coversheet' && selectedFiles?.length > 10 || this.section === 'coversheet' && allAvailableFiles > 10) {
        this.resetFileInput();
        this.multipleFilesErrorMessage = CREDIT_APP_TOAST_MESSAGES.max10FilesLimit;
        this.files = null;
        return;
      }
      const isValid = this.validateMulipleFiles(selectedFiles);
      if (isValid) {
        this.addFiles(input?.files);
      } else {
        this.resetFileInput();
      }
    }
  }

  /**
   * isUploadDisabled() - getter to set the value of true or false to disable the upload file after a file is uploaded
   */
  get isUploadDisabled(): boolean {
    if (this.section === 'coversheet') {
      return this.files?.length >= 10 && this.fileUploadSuccess;
    }
    if (this.section === 'sendloa') {
      return this.files?.length >= 5 && this.fileUploadSuccess;
    }
    return this.files?.length > 0 && this.fileUploadSuccess;
  }

  /**
   * addFiles() - Adds new files to the current list and ensures uniqueness by name.
   * @param files - The FileList containing the files to be added.
   */
  addFiles(files: FileList) {
    const newFiles = Array?.from(files);
    if (!Array.isArray(this.files)) {
      this.files = [];
    }
    this.files = _.uniqBy([...this.files, ...newFiles], 'name');

    if (this.section === 'sendloa') {
      const isSizeWithinLimit = this.validateCombinedFileSize();
      if (isSizeWithinLimit) {
        this.emitFiles();
        this.fileExceedsLimit.emit({ disable: false });
      } else {
        this.fileExceedsLimit.emit({ disable: true });
      }

    } else {
      this.emitFiles();
    }

  }

  /**
   * emitFiles() - Emits the updated files list to the parent component.
   */
  emitFiles() {
    this.filesUpdated.emit(this.files);
  }

  /**
   * removeFile() - Removes a file from the current list and emits the removed file.
   * @param event - The click event.
   * @param index - The index of the file to be removed.
   */
  removeFile(event: Event, index: number) {
    event.preventDefault();
    const removedFile = this.files[index]; // Get the file to be removed
    this.files.splice(index, 1);
    this.filesChanged.emit([removedFile]); // Emit the removed file as an array
    this.fileRemoved.emit(this.files);
    this.resetFileInput();

    if (this.section === 'sendloa') {
      this.combinedSize = this.combinedSize - removedFile.size;
      const isSizeWithinLimit = this.validateCombinedFileSize();
      if (isSizeWithinLimit) {
        this.fileExceedsLimit.emit({ disable: false });
      } else {
        this.fileExceedsLimit.emit({ disable: true });
      }
    }
  }

  /**
   * resetFileInput() - Resets the file input element to allow re-selection of the same file.
   */
  resetFileInput(): void {
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  /**
   * viewFile() - Opens the selected file in a new browser tab for viewing.
   * @param file - The file to be viewed.
   */
  viewFile(file: File): void {
    const fileURL = URL.createObjectURL(file);
    if (this.viewFileExternal) {
      this.viewFileCallback.emit(this.section);
    } else {
      window.open(fileURL, '_blank'); // Open the file in a new tab
    }
  }

  /**
   * validateMulipleFiles() - Method to handle the validation for multiple files
   * @param fileList files array
   * @returns boolean
   */

  validateMulipleFiles(fileList: FileList): boolean {
  for (let i = 0; i < fileList.length; i++) {
    const file = fileList[i];
    if (!file?.name) {
      return false;
    }

    if (this.isFileSizeExceeded(file)) {
      this.handleFileSizeExceeded();
      return false;
    }

    if (!this.isPdfFile(file)) {
      this.handleInvalidPdf();
      return false;
    }

    if (!this.isValidFileName(file.name)) {
      this.handleInvalidFileName();
      return false;
    }
  }
  return true;
}
private isFileSizeExceeded(file: File): boolean {
  return file.size > this.MAX_FILE_SIZE;
}
private handleFileSizeExceeded(): void {
  this.resetFileInput();
  this.fileSizeErrorMessage =
    this.section === 'sendloa' || this.section === 'projectCoi'
      ? CREDIT_APP_TOAST_MESSAGES.fileMoreThan20MBErrMsg
      : this.is25MBSection()
        ? CREDIT_APP_TOAST_MESSAGES.fileMoreThan25MBErrMsg
        : CREDIT_APP_TOAST_MESSAGES.fileMoreThan5MBErrMsg;

  if (this.section === 'sendloa' || this.section === 'projectCoi') {
    this.callbackMessage.emit({
      message: FILE_UPLOAD_TOAST_MESSAGES.sizeLimit20MBErrMsg
    });
  } else {
    this.callbackMessage.emit({
      message: FILE_UPLOAD_TOAST_MESSAGES.sizeLimit25MBErrMsg
    });
  }

  this.multipleFilesErrorMessage = null;
  this.pdfFileErrorMessage = null;
  this.files = this.files || [];
}
  private is25MBSection(): boolean {
    return (
      this.section === 'bank' ||
      this.section === 'trade' ||
      this.section === 'coversheet' ||
      this.section === 'letterofguarantee' ||
      this.section === 'certificateOfInsurance' ||
      this.section === 'sendloa'
    );
  }
  private isPdfFile(file: File): boolean {
    return file.type === 'application/pdf';
  }

  private handleInvalidPdf(): void {
    this.resetFileInput();
    this.pdfFileErrorMessage = CREDIT_APP_TOAST_MESSAGES.selectPdfFile;
    this.callbackMessage.emit({
      message: FILE_UPLOAD_TOAST_MESSAGES.invalidFileType
    });
    this.fileSizeErrorMessage = null;
    this.multipleFilesErrorMessage = null;
    this.files = null;
  }
  private isValidFileName(fileName: string): boolean {
    const validFileNameRegex = /^[a-zA-Z0-9_\-\s]+\.pdf$/;
    return validFileNameRegex.test(fileName);
  }

  private handleInvalidFileName(): void {
    this.resetFileInput();
    this.fileNameErrorMessage = CREDIT_APP_TOAST_MESSAGES.invalidFileName;
    this.files = null;
  }

  /**
   * validateCombinedFileSize() - Method to validate for the combined file size
   * @returns boolean
   */
  validateCombinedFileSize(): any {
    this.combinedSize = this.getTotalFileSize();
    if (this.combinedSize > this.MAX_FILE_SIZE) {
      this.callbackMessage.emit({ message: FILE_UPLOAD_TOAST_MESSAGES.combinedSizeLimit });
      this.multipleFilesErrorMessage = null;
      this.pdfFileErrorMessage = null;
      this.files = this.files || [];
      return false;
    }
    return true;
  }

  /**
   * getTotalFileSize() - Method to get the file size
   * @returns consolidated file size
   */
  getTotalFileSize(): number {
    return _.sumBy(this.files, file => file.size);
  }
}
